import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'explore-mvp-launch-checklist-v1';

const SECTIONS = [
  {
    id: 'user-flow',
    title: 'User Flow',
    items: [
      { id: 'uf-home', label: 'Home loads; hero search and world grid work' },
      { id: 'uf-worlds', label: 'World Explorer opens from Animals / Robotics / Math cards' },
      { id: 'uf-free', label: 'First lesson per world opens (mini-app or search fallback)' },
      { id: 'uf-locked', label: 'Locked lessons do not start adventures (button disabled + guard)' },
      { id: 'uf-complete', label: 'Completing a free lesson shows Completed; persists after refresh' },
      { id: 'uf-search', label: 'Supervised web search route (/s) runs after keyword check when used' },
      { id: 'uf-chat', label: 'Learning assistant (/chat) loads; chips and send behave as expected' },
      { id: 'uf-settings', label: 'Settings and sign-in flows do not crash when opened' },
    ],
  },
  {
    id: 'content',
    title: 'Content',
    items: [
      { id: 'co-worlds', label: 'Each world shows three learning paths with correct titles' },
      { id: 'co-engines', label: 'Activities use only approved engines (quiz, sequence, match, cards, pattern)' },
      { id: 'co-copy', label: 'No placeholder lorem; carousel shows honest “coming later” messaging' },
      { id: 'co-trust', label: 'Parent overview blocks visible on home, topics, and chat where intended' },
    ],
  },
  {
    id: 'safety',
    title: 'Safety',
    items: [
      { id: 'sa-keyword', label: 'Keyword / safety API or fallback allows or blocks as designed' },
      { id: 'sa-child', label: 'Wording is child-appropriate; no “AI friend” or companion framing' },
      { id: 'sa-chat', label: 'Chat and search helpers do not promise unlimited or unrestricted AI' },
      { id: 'sa-restricted', label: '/restricted page reachable if age/region gating is used' },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy',
    items: [
      { id: 'pr-footer', label: 'Footer states supervised educational use; not a social network' },
      { id: 'pr-progress', label: 'Progress stored locally only (localStorage) for this MVP' },
      { id: 'pr-analytics', label: 'Analytics disclosure reviewed; parents aware if GA is on' },
    ],
  },
  {
    id: 'monetization',
    title: 'Monetization',
    items: [
      { id: 'mo-premium', label: 'Premium gate copy present; no fake payment or checkout' },
      { id: 'mo-ads', label: 'Product copy does not claim third-party ads in the app experience' },
      { id: 'mo-free', label: 'Free tier = first lesson per world only; matches accessConfig behavior' },
    ],
  },
  {
    id: 'mobile',
    title: 'Mobile QA',
    items: [
      { id: 'mb-viewport', label: 'Viewport meta present; no horizontal scroll on home/topics' },
      { id: 'mb-sidebar', label: 'Sidebar / hamburger usable; main content not covered' },
      { id: 'mb-touch', label: 'Primary buttons and lesson cards usable on narrow widths' },
      { id: 'mb-inputs', label: 'Search inputs use ≥16px font on small screens where applicable' },
    ],
  },
  {
    id: 'deployment',
    title: 'Deployment',
    items: [
      { id: 'dp-build', label: 'Production build succeeds (npm run build)' },
      { id: 'dp-api', label: 'VITE_API_TARGET / proxy verified for keyword and APIs' },
      { id: 'dp-routes', label: 'Legacy /search-result redirects still tested if applicable' },
    ],
  },
];

function loadChecked() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    return data.checked && typeof data.checked === 'object' ? data.checked : {};
  } catch {
    return {};
  }
}

function saveChecked(map) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ checked: map, savedAt: new Date().toISOString() }),
    );
  } catch {
    /* ignore quota */
  }
}

const pageStyle = {
  minHeight: '100vh',
  padding: '24px 18px 48px',
  maxWidth: 720,
  margin: '0 auto',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  boxSizing: 'border-box',
};

const titleStyle = {
  fontSize: 'clamp(1.35rem, 4vw, 1.65rem)',
  fontWeight: 800,
  marginBottom: 8,
};

const introStyle = {
  fontSize: 15,
  lineHeight: 1.5,
  opacity: 0.8,
  marginBottom: 24,
};

const sectionTitleStyle = {
  fontSize: '1.05rem',
  fontWeight: 800,
  marginTop: 28,
  marginBottom: 12,
  paddingBottom: 8,
  borderBottom: '1px solid var(--color-border)',
};

const rowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  marginBottom: 10,
  fontSize: 14,
  lineHeight: 1.45,
};

const cbStyle = {
  marginTop: 3,
  flexShrink: 0,
};

const actionsStyle = {
  marginTop: 32,
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'center',
};

export default function LaunchChecklistPage() {
  const [checked, setChecked] = useState(loadChecked);

  useEffect(() => {
    setChecked(loadChecked());
  }, []);

  const toggle = useCallback((id) => {
    setChecked((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveChecked(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setChecked({});
    saveChecked({});
  }, []);

  const allIds = SECTIONS.flatMap((s) => s.items.map((i) => i.id));
  const done = allIds.filter((id) => checked[id]).length;

  return (
    <div style={pageStyle}>
      <Link
        to="/"
        style={{
          display: 'inline-block',
          marginBottom: 20,
          fontWeight: 600,
          color: 'var(--color-primary)',
          textDecoration: 'none',
        }}
      >
        ← Home
      </Link>
      <h1 style={titleStyle}>MVP launch checklist</h1>
      <p style={introStyle}>
        FTC-conscious review for Explore. Check items as you verify locally. State is saved in this browser (
        <code style={{ fontSize: 13 }}>{STORAGE_KEY}</code>).
      </p>
      <p style={{ ...introStyle, marginTop: -12 }}>
        Progress: <strong>{done}</strong> / {allIds.length}
      </p>

      {SECTIONS.map((section) => (
        <section key={section.id} aria-labelledby={`lc-${section.id}`}>
          <h2 id={`lc-${section.id}`} style={sectionTitleStyle}>
            {section.title}
          </h2>
          {section.items.map((item) => (
            <label key={item.id} style={{ ...rowStyle, cursor: 'pointer' }}>
              <input
                type="checkbox"
                style={cbStyle}
                checked={!!checked[item.id]}
                onChange={() => toggle(item.id)}
              />
              <span>{item.label}</span>
            </label>
          ))}
        </section>
      ))}

      <div style={actionsStyle}>
        <button type="button" className="btn" onClick={clearAll}>
          Clear all checks
        </button>
      </div>
    </div>
  );
}
